import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import type { ReactNode } from "react";

import { fetchDeliveryAddresses } from "../../api/deliveryAddresses";
import type {
  PriceAnomalyStatus,
  PriceVarianceFilters,
  UserVarianceRow,
} from "../../api/reports";
import DateInput from "../../components/DateInput";
import DeliveryAddressMultiSelect from "../../components/DeliveryAddressMultiSelect";
import FilterDropdown from "../../components/FilterDropdown";
import MultiSelectChips from "../../components/MultiSelectChips";
import type { MultiSelectOption } from "../../components/MultiSelectChips";
import SegmentedControl from "../../components/SegmentedControl";
import { REVIEW_STATUS_LABELS } from "../../lib/reviewStatusLabels";
import { useEscapeKey } from "../../lib/useEscapeKey";

import type { DeliveryKind, PriceVariancePreset } from "./types";

type UpdateFilter = <K extends keyof PriceVarianceFilters>(
  key: K,
  value: PriceVarianceFilters[K] | undefined,
) => void;

const DELIVERY_KIND_OPTIONS = [
  { value: "beides" as const, label: "Beides" },
  { value: "lager" as const, label: "Lager" },
  { value: "baustellen" as const, label: "Baustellen" },
];

const PRICE_ANOMALY_STATUS_OPTIONS = [
  { value: "open", label: REVIEW_STATUS_LABELS.open },
  { value: "in_review", label: REVIEW_STATUS_LABELS.in_review },
  { value: "resolved", label: REVIEW_STATUS_LABELS.resolved },
  { value: "", label: "Alle" },
];

const PRICE_ANOMALY_STATUS_LABELS: Record<PriceAnomalyStatus, string> =
  REVIEW_STATUS_LABELS;

type ActiveContextFilter = {
  key: string;
  label: string;
  value: string;
  onClear: () => void;
};

export function PriceVarianceFiltersPanel({
  open,
  onClose,
  preset,
  filters,
  updateFilter,
  users,
  projectOptions,
  projectsLoading,
  manufacturerOptions,
  manufacturersLoading,
  supplierOptions,
  suppliersLoading,
  deliveryKind,
  onDeliveryKindChange,
  onResetAll,
  analysis,
}: {
  /**
   * Analyse: period, wholesaler and manufacturer sit above the tabs, and
   * "all statuses" is the default – only what differs shows as a chip.
   */
  analysis?: { namedPeriod: boolean };
  open: boolean;
  onClose: () => void;
  preset: PriceVariancePreset | null;
  filters: PriceVarianceFilters;
  updateFilter: UpdateFilter;
  users: UserVarianceRow[] | undefined;
  projectOptions: MultiSelectOption[];
  projectsLoading: boolean;
  manufacturerOptions: MultiSelectOption[];
  manufacturersLoading: boolean;
  supplierOptions: MultiSelectOption[];
  suppliersLoading: boolean;
  deliveryKind: DeliveryKind;
  onDeliveryKindChange: (next: DeliveryKind) => void;
  onResetAll: () => void;
}) {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLElement | null>(null);
  useEscapeKey(onClose, open);
  useEffect(() => {
    if (!open) return;
    closeButtonRef.current?.focus();
    const dialog = dialogRef.current;
    if (!dialog) return;
    function trapFocus(event: KeyboardEvent) {
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        dialog!.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => element.offsetParent !== null);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !dialog!.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !dialog!.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }
    dialog.addEventListener("keydown", trapFocus);
    return () => dialog.removeEventListener("keydown", trapFocus);
  }, [open]);

  const userOptions = useMemo(
    () => [
      { value: "", label: "alle" },
      ...(users ?? []).map((user) => ({
        value: user.online_user_email,
        label: user.online_user_name ?? user.online_user_email,
      })),
    ],
    [users],
  );
  const deliveryAddressesQuery = useQuery({
    queryKey: ["delivery-addresses", "all"],
    queryFn: () => fetchDeliveryAddresses({ limit: 1000 }),
    staleTime: 300_000,
  });
  const projectLabelByKey = useOptionLabelMap(projectOptions);
  const manufacturerLabelByKey = useOptionLabelMap(manufacturerOptions);
  const supplierLabelByKey = useOptionLabelMap(supplierOptions);
  const deliveryAddressLabelByKey = useMemo(
    () =>
      new Map(
        (deliveryAddressesQuery.data?.items ?? []).map((item) => [
          item.delivery_address_key,
          item.delivery_address_display ?? item.delivery_address_key,
        ]),
      ),
    [deliveryAddressesQuery.data],
  );
  const userLabelByEmail = useMemo(
    () =>
      new Map(
        (users ?? []).map((user) => [
          user.online_user_email,
          user.online_user_name ?? user.online_user_email,
        ]),
      ),
    [users],
  );
  const activeContextFilters = useMemo<ActiveContextFilter[]>(() => {
    const items: ActiveContextFilter[] = [];
    const datesFromPeriod = analysis?.namedPeriod ?? false;
    if (filters.from && !datesFromPeriod) {
      items.push({
        key: "from",
        label: "Von",
        value: filters.from,
        onClear: () => updateFilter("from", undefined),
      });
    }
    if (filters.to && !datesFromPeriod) {
      items.push({
        key: "to",
        label: "Bis",
        value: filters.to,
        onClear: () => updateFilter("to", undefined),
      });
    }
    if (filters.online_user) {
      items.push({
        key: `online_user:${filters.online_user}`,
        label: "Besteller",
        value: userLabelByEmail.get(filters.online_user) ?? filters.online_user,
        onClear: () => updateFilter("online_user", undefined),
      });
    }
    const defaultPriceAnomalyStatus =
      analysis || preset === "historical_spikes" ? undefined : "open";
    if (filters.reference_quality !== "insufficient" && filters.price_anomaly_status !== defaultPriceAnomalyStatus) {
      items.push({
        key: `price-anomaly-status:${filters.price_anomaly_status ?? "all"}`,
        label: "Status",
        value: filters.price_anomaly_status
          ? PRICE_ANOMALY_STATUS_LABELS[filters.price_anomaly_status]
          : "Alle",
        onClear: () =>
          updateFilter("price_anomaly_status", defaultPriceAnomalyStatus),
      });
    }
    for (const articleKey of filters.article ?? []) {
      items.push({
        key: `article:${articleKey}`,
        label: "Artikel",
        value: articleKey.replace(/^article:/, ""),
        onClear: () =>
          updateFilter("article", withoutValue(filters.article, articleKey)),
      });
    }
    for (const projectKey of filters.project ?? []) {
      items.push({
        key: `project:${projectKey}`,
        label: "Projekt",
        value: projectLabelByKey.get(projectKey) ?? projectKey,
        onClear: () =>
          updateFilter("project", withoutValue(filters.project, projectKey)),
      });
    }
    for (const deliveryKey of filters.delivery_address_key ?? []) {
      items.push({
        key: `delivery:${deliveryKey}`,
        label: "Lieferadresse",
        value: deliveryAddressLabelByKey.get(deliveryKey) ?? deliveryKey,
        onClear: () =>
          updateFilter(
            "delivery_address_key",
            withoutValue(filters.delivery_address_key, deliveryKey),
          ),
      });
    }
    for (const manufacturerId of analysis ? [] : filters.manufacturer_id ?? []) {
      items.push({
        key: `manufacturer:${manufacturerId}`,
        label: "Hersteller",
        value: manufacturerLabelByKey.get(manufacturerId) ?? manufacturerId,
        onClear: () =>
          updateFilter(
            "manufacturer_id",
            withoutValue(filters.manufacturer_id, manufacturerId),
          ),
      });
    }
    for (const supplier of analysis ? [] : filters.supplier ?? []) {
      items.push({
        key: `supplier:${supplier}`,
        label: "Großhändler",
        value: supplierLabelByKey.get(supplier) ?? supplier,
        onClear: () =>
          updateFilter("supplier", withoutValue(filters.supplier, supplier)),
      });
    }
    if (deliveryKind !== "beides") {
      items.push({
        key: `delivery-kind:${deliveryKind}`,
        label: "Lieferart",
        value: deliveryKind === "lager" ? "Lager" : "Baustellen",
        onClear: () => onDeliveryKindChange("beides"),
      });
    }
    return items;
  }, [
    analysis,
    deliveryKind,
    deliveryAddressLabelByKey,
    filters.article,
    filters.delivery_address_key,
    filters.from,
    filters.manufacturer_id,
    filters.online_user,
    filters.price_anomaly_status,
    filters.project,
    filters.supplier,
    filters.to,
    manufacturerLabelByKey,
    onDeliveryKindChange,
    preset,
    projectLabelByKey,
    supplierLabelByKey,
    updateFilter,
    userLabelByEmail,
  ]);

  return (
    <>
      <ActiveContextFilters items={activeContextFilters} />
      {open ? (
        <div className="fixed inset-0 z-50">
          <div
            aria-hidden="true"
            className="absolute inset-0 cursor-default bg-slate-900/20"
            onClick={onClose}
          />
          <aside
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="price-variance-filter-title"
            className="absolute inset-y-0 right-0 flex w-[min(42rem,100vw)] flex-col border-l border-slate-200 bg-white shadow-2xl"
          >
            <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Mehrkosten
                </div>
                <h2
                  id="price-variance-filter-title"
                  className="mt-1 text-lg font-semibold text-slate-900"
                >
                  Weitere Filter
                </h2>
              </div>
              <button
                ref={closeButtonRef}
                type="button"
                onClick={onClose}
                className="h-8 rounded-md px-2 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              >
                Schließen
              </button>
            </header>

            <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-5 py-5">
              <FilterSection
                title="Zeitraum und Bearbeitung"
                description="Belegdatum, Besteller und aktueller Prüfstatus."
              >
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="flex flex-col gap-1 text-xs text-slate-600">
                    Von
                    <DateInput
                      value={filters.from ?? ""}
                      onChange={(next) =>
                        updateFilter("from", next || undefined)
                      }
                      ariaLabel="Von-Datum wählen"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs text-slate-600">
                    Bis
                    <DateInput
                      value={filters.to ?? ""}
                      onChange={(next) => updateFilter("to", next || undefined)}
                      ariaLabel="Bis-Datum wählen"
                    />
                  </label>
                  <div className="flex flex-col gap-1 text-xs text-slate-600">
                    Besteller
                    <FilterDropdown
                      label="Besteller"
                      ariaLabel="Besteller filtern"
                      options={userOptions}
                      value={filters.online_user ?? ""}
                      defaultValue=""
                      showValue
                      onChange={(next) =>
                        updateFilter("online_user", next || undefined)
                      }
                    />
                  </div>
                  {filters.reference_quality !== "insufficient" ? <div className="flex flex-col gap-1 text-xs text-slate-600">
                    Prüfstatus
                    <FilterDropdown
                      label="Prüfstatus"
                      ariaLabel="Preisprüfungs-Status filtern"
                      options={PRICE_ANOMALY_STATUS_OPTIONS}
                      value={filters.price_anomaly_status ?? ""}
                      defaultValue={
                        preset === "historical_spikes" ? "" : "open"
                      }
                      showValue
                      onChange={(next) =>
                        updateFilter(
                          "price_anomaly_status",
                          next ? (next as PriceAnomalyStatus) : undefined,
                        )
                      }
                    />
                  </div> : null}
                </div>
              </FilterSection>

              <FilterSection
                title="Artikel und Einkauf"
                description="Artikelherkunft und Lieferant der Position."
              >
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="flex flex-col gap-1 text-xs text-slate-600">
                    Hersteller
                    <MultiSelectChips
                      value={filters.manufacturer_id ?? []}
                      onChange={(next) => updateFilter("manufacturer_id", next)}
                      options={manufacturerOptions}
                      isLoading={manufacturersLoading}
                      placeholder="Hersteller suchen …"
                      maxVisibleChips={2}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs text-slate-600">
                    Großhändler
                    <MultiSelectChips
                      value={filters.supplier ?? []}
                      onChange={(next) => updateFilter("supplier", next)}
                      options={supplierOptions}
                      isLoading={suppliersLoading}
                      placeholder="Großhändler suchen …"
                      maxVisibleChips={2}
                      emptyHint="Keine passenden Großhändler."
                    />
                  </label>
                </div>
              </FilterSection>

              <FilterSection
                title="Kostenstelle und Lieferung"
                description="Projekt, Lieferadresse und Lager- oder Baustellenlieferung."
              >
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="flex flex-col gap-1 text-xs text-slate-600 sm:col-span-2">
                    Projekt
                    <MultiSelectChips
                      value={filters.project ?? []}
                      onChange={(next) => updateFilter("project", next)}
                      options={projectOptions}
                      isLoading={projectsLoading}
                      placeholder="Projekt suchen …"
                      maxVisibleChips={2}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs text-slate-600 sm:col-span-2">
                    Lieferadresse
                    <DeliveryAddressMultiSelect
                      value={filters.delivery_address_key ?? []}
                      onChange={(next) =>
                        updateFilter("delivery_address_key", next)
                      }
                    />
                  </label>
                  <div className="flex flex-col gap-1 text-xs text-slate-600 sm:col-span-2">
                    Lieferart
                    <SegmentedControl<DeliveryKind>
                      value={deliveryKind}
                      options={DELIVERY_KIND_OPTIONS}
                      onChange={onDeliveryKindChange}
                      ariaLabel="Lieferart filtern"
                    />
                  </div>
                </div>
              </FilterSection>
            </div>

            <footer className="flex items-center justify-between gap-3 border-t border-slate-200 px-5 py-4">
              <button
                type="button"
                onClick={onResetAll}
                className="h-9 rounded-md px-3 text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              >
                Alle Filter zurücksetzen
              </button>
              <button
                type="button"
                onClick={onClose}
                className="h-9 rounded-md bg-emerald-600 px-4 text-sm font-medium text-white hover:bg-emerald-700"
              >
                Fertig
              </button>
            </footer>
          </aside>
        </div>
      ) : null}
    </>
  );
}

function FilterSection({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section>
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      <p className="mt-1 text-xs leading-5 text-slate-500">{description}</p>
      <div className="mt-3">{children}</div>
    </section>
  );
}

export function countActivePriceVarianceFilters(
  filters: PriceVarianceFilters,
  deliveryKind: DeliveryKind,
  preset: PriceVariancePreset | null,
): number {
  const defaultPriceAnomalyStatus =
    preset === "historical_spikes" ? undefined : "open";
  return [
    Boolean(filters.from),
    Boolean(filters.to),
    Boolean(filters.online_user),
    filters.price_anomaly_status !== defaultPriceAnomalyStatus,
    Boolean(filters.article?.length),
    Boolean(filters.project?.length),
    Boolean(filters.delivery_address_key?.length),
    Boolean(filters.manufacturer_id?.length),
    Boolean(filters.supplier?.length),
    deliveryKind !== "beides",
  ].filter(Boolean).length;
}

function useOptionLabelMap(
  options: readonly MultiSelectOption[],
): ReadonlyMap<string, string> {
  return useMemo(
    () => new Map(options.map((option) => [option.key, option.label])),
    [options],
  );
}

function withoutValue(
  values: readonly string[] | undefined,
  value: string,
): string[] {
  return (values ?? []).filter((candidate) => candidate !== value);
}

function ActiveContextFilters({
  items,
}: {
  items: ActiveContextFilter[];
}) {
  if (items.length === 0) return null;
  return (
    <div
      aria-label="Aktive Kontextfilter"
      className="relative z-10 mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3 text-xs text-slate-600"
    >
      <span>Zusätzliche Filter:</span>
      {items.map((item) => (
        <span
          key={item.key}
          className="inline-flex min-h-8 w-full max-w-full items-center gap-1 rounded bg-emerald-50 px-2 py-0.5 text-emerald-800 sm:w-auto"
        >
          <span className="font-medium">{item.label}:</span>
          <span className="min-w-0 flex-1 truncate font-mono sm:max-w-[16rem]">
            {item.value}
          </span>
          <button
            type="button"
            onClick={item.onClear}
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-emerald-700 hover:bg-emerald-100 hover:text-emerald-950"
            aria-label={`${item.label} ${item.value} entfernen`}
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
}
